import { Component, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AuditChannelService } from 'src/app/core/service/audit-channel.service';
import { CaseService } from 'src/app/core/service/case.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';

@Component({
  standalone: false,
  selector: 'app-channel-audit-details',
  templateUrl: './channel-audit-details.component.html',
  styleUrl: './channel-audit-details.component.scss'
})
export class ChannelAuditDetailsComponent implements OnDestroy
{
  public currentChannelId: number;
  public currentRevisionId: number;
  public currentAuditType: number;    // 0=Channel, 1=Users, 2=Groups, 3=Sensitivity, 4=Goals
  public currentRevisionType: number; // 0=Create, 1=Update, 2=Delete
  public currentChangesOnly = false;
  public currentEnableAdvanced = false;

  public view: GridViewManager;
  public data = [];
  public pageSizes = [5, 10, 25, 50, 100];
  public count: number;
  public columnsConfig: ColumnSettings[];

  private messagingSubscription: Subscription;

  /**
   * Constructor
   */
  constructor(
    private auditChannelService: AuditChannelService,
    private translateService: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private caseService: CaseService)
  {
    this.auditChannelService = auditChannelService;
    this.translateService = translateService;
    this.messageHandlerService = messageHandlerService;

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'refreshAuditChannelDetails')
        {
          this.initGridColumnsAndLoadData();
        }
      }
    });
  }

  @Input() public set channelId(channelId: number)
  {
    if (channelId !== undefined)
    {
      this.currentChannelId = channelId;
    }
  }

  @Input() public set revisionId(revisionId: number)
  {
    if (revisionId !== undefined)
    {
      this.currentRevisionId = revisionId;

      if (this.view !== undefined)
      {
        this.view.startLoading();
      }

      this.view = new GridViewManager(99999);
      this.view.setData(this.data);

      this.initGridColumnsAndLoadData();
    }
  }

  @Input() public set enableAdvanced(enableAdvanced: boolean)
  {
    if (enableAdvanced !== undefined)
    {
      this.currentEnableAdvanced = enableAdvanced;
    }
  }

  @Input() public set changesOnly(changesOnly: boolean)
  {
    if (changesOnly !== undefined)
    {
      this.currentChangesOnly = changesOnly;
    }
  }

  @Input() public set revisionType(revisionType: number)
  {
    if (revisionType !== undefined)
    {
      this.currentRevisionType = revisionType;
    }
  }

  @Input() public set auditType(auditType: number)
  {
    if (auditType !== undefined)
    {
      this.currentAuditType = auditType;
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Get audit details for audit_channel 
   **/ 
  public getAuditChannelDetails(): void
  {
    this.auditChannelService.getAuditChannelDetails('channel', this.currentChannelId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.data = [];
        let index = 0;
        data.forEach((d: any) =>
        {
          if (index === 0)
          {
            Object.keys(d).forEach(e =>
            {
              let propertyNameText = null;
              if (e !== 'id' && e !== 'revisionId' && e !== 'caseManagerAssignmentModeId'
                && e !== 'monitorName')
              {
                if ((!this.currentChangesOnly) ||
                  ((this.currentChangesOnly && data.length === 2 && d[e] !== data[1][e])) || /* Update */
                  ((this.currentChangesOnly && data.length === 1)))                          /* Create */
                {
                  // Property Name
                  switch (e)
                  {
                    case 'appId':
                      propertyNameText = this.translateService.instant('APP-ID');
                      break;
                    case 'bpmId':
                      propertyNameText = this.translateService.instant('BPM-ID');
                      break;
                    case 'bpmKey':
                      propertyNameText = this.translateService.instant('BPM-KEY');
                      break;
                    case 'procDefId':
                      propertyNameText = this.translateService.instant('PROC-DEF-ID');
                      break;
                    case 'procDefKey':
                      propertyNameText = this.translateService.instant('PROC-DEF-KEY');
                      break;
                    case 'bpmName':
                      propertyNameText = this.translateService.instant('BPM-NAME');
                      break;
                    case 'active':
                      propertyNameText = this.translateService.instant('ACTIVE');
                      break;
                    case 'caseManagerAssignmentMode':
                      propertyNameText = this.translateService.instant('CASE-MANAGER-ASSIGNMENT');
                      break;
                    case 'comments':
                      propertyNameText = this.translateService.instant('COMMENTS');
                      break;
                    case 'description':
                      propertyNameText = this.translateService.instant('DESCRIPTION');
                      break;
                    case 'displayOrder':
                      propertyNameText = this.translateService.instant('DISPLAY-ORDER');
                      break;
                    case 'isDashboardEnrollment':
                      propertyNameText = this.translateService.instant('DASHBOARD-ENROLLMENT');
                      break;
                    case 'isLimitedEnrollment':
                      propertyNameText = this.translateService.instant('LIMITED-ENROLLMENT');
                      break;
                    case 'isLimitedView':
                      propertyNameText = this.translateService.instant('LIMITED-VIEW');
                      break;
                    case 'isPrivate':
                      propertyNameText = this.translateService.instant('PRIVATE');
                      break;
                    case 'isReferral':
                      propertyNameText = this.translateService.instant('REFERRAL');
                      break;
                    case 'isSensitive':
                      propertyNameText = this.translateService.instant('SENSITIVE');
                      break;
                    case 'name':
                      propertyNameText = this.translateService.instant('NAME');
                      break;
                    case 'status':
                      propertyNameText = this.translateService.instant('STATUS');
                      break;
                    case 'categoryName':
                      propertyNameText = this.translateService.instant('CATEGORY');
                      break;
                    default:
                      propertyNameText = e;
                      break;
                  }
                  // Property Value
                  let newValueText = null;
                  let previousValueText = null;
                  switch (e)
                  {
                    case 'active':
                    case 'isDashboardEnrollment':
                    case 'isLimitedEnrollment':
                    case 'isLimitedView':
                    case 'isPrivate':
                    case 'isReferral':
                    case 'isSensitive':
                      {
                        newValueText = d[e] ? this.translateService.instant('YES') : this.translateService.instant('NO');
                        if (data.length === 2)
                        {
                          previousValueText = data[1][e] ? this.translateService.instant('YES') : this.translateService.instant('NO');
                        }
                        break;
                      }
                    case 'caseManagerAssignmentMode':
                      newValueText = d[e] ? this.caseService.getAssigmentModeText(d[e]) : '';
                      if (data.length === 2)
                      {
                        previousValueText = data[1][e] ? this.caseService.getAssigmentModeText(data[1][e]) : '';
                      }
                      break;
                  }
                  this.data.push({
                    propertyName: propertyNameText,
                    newValue: newValueText !== null ? newValueText : d[e],
                    previousValue: previousValueText !== null ? (data.length === 2 ? previousValueText : '') : (data[1] ? data[1][e] : '')
                  });
                }
              }
            });
          }
          index++;
        });
        this.view.setData(this.data);
        this.view.setSort([{ field: 'propertyName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Get audit details for audit_channel_goals
   **/ 
  public getAuditGoalsDetails(): void
  {
    this.auditChannelService.getAuditChannelDetails('channel/goals', this.currentChannelId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.data = [];
        let index = 0;
        data.forEach((d: any) =>
        {
          if (index === 0)
          {
            Object.keys(d).forEach(e =>
            {
              let propertyNameText = null;
              if (e !== 'id' && e !== 'revisionId')
              {
                if ((!this.currentChangesOnly) ||
                  ((this.currentChangesOnly && data.length === 2 && d[e] !== data[1][e])) || /* Update */
                  ((this.currentChangesOnly && data.length === 1)))                          /* Create */
                {
                  // Property Name
                  switch (e)
                  {
                    case 'success':
                      propertyNameText = this.translateService.instant('PROGRAM-OVERALL-SUCCESS');
                      break;
                    case 'timelineDays':
                      propertyNameText = this.translateService.instant('PROGRAM-SUCCESS-TIME');
                      break;
                    default:
                      propertyNameText = e;
                      break;
                  }
                  // Property Value
                  this.data.push({
                    propertyName: propertyNameText,
                    newValue:  d[e],
                    previousValue: data[1] ? data[1][e] : ''
                  });
                }
              }
            });
          }
          index++;
        });
        this.view.setData(this.data);
        this.view.setSort([{ field: 'propertyName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Get audit details for audit_channel_users, audit_channel_groups and audit_channel_sensitivity
   **/ 
  public getAuditChannelNameDetails(): void
  {
    let detailTypeUrl = null;
    let propertyText = null;
    switch (this.currentAuditType)
    {
      case 1: //  Users
        propertyText = this.translateService.instant('USER-NAME');
        detailTypeUrl = 'channel/users';
        break;
      case 2: // Groups
        propertyText = this.translateService.instant('GROUP-NAME');
        detailTypeUrl = 'channel/groups';
        break;
      case 3: // Restricted Data Reasons
        propertyText = this.translateService.instant('SENSITIVITY-NAME');
        detailTypeUrl = 'channel/restrictions';
        break;
      default:
        return;
    }

    this.auditChannelService.getAuditChannelDetails(detailTypeUrl, this.currentChannelId, this.currentRevisionId, this.currentRevisionType).subscribe({
      next: (data: any) =>
      {
        this.count = data.length;
        this.data = [];
        data.forEach((d: any) =>
        {
          const value = {
            propertyName: propertyText,
            newValue: d.name,
            previousValue: ''
          }
          this.data.push(value);
        });
        this.view.setData(this.data);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  private initGridColumns(res: any): void
  {
    // currentAuditType: 0=Channel, 1=Users, 2=Groups, 3=Sensitivity, 4=Goals
    // currentRevisionType: 0=Create, 1=Update, 2=Delete
    this.columnsConfig = [
      {
        field: 'propertyName', title: res['PROPERTY-NAME'], titleKeys: ['PROPERTY-NAME'],
        width: 20, sortable: true, hidden: false, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'newValue',
        title: this.currentAuditType === 1 && ((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) ? res.VALUE : res['NEW-VALUE'],
        titleKeys: this.currentAuditType === 1 && ((this.currentRevisionType === 0) || (this.currentRevisionType === 2)) ? res.VALUE : res['NEW-VALUE'],
        width: 40, sortable: true, hidden: false, filterable: this.currentEnableAdvanced, dataType: 'string'
      },
      {
        field: 'previousValue', title: res['PREVIOUS-VALUE'], titleKeys: ['PREVIOUS-VALUE'],
        width: 40, sortable: true, hidden: this.currentRevisionType === 0 || ((this.currentAuditType === 1) && (this.currentRevisionType === 2)), filterable: this.currentEnableAdvanced, dataType: 'string'
      }
    ];
  }

  private initGridColumnsAndLoadData(): void
  {
    this.translateService.get(['PROPERTY-NAME', 'NEW-VALUE', 'PREVIOUS-VALUE', 'VALUE', 'NAME', 'NEW-NAME']).subscribe({
      next: (res) =>
      {
        this.initGridColumns(res);
        if (this.currentAuditType === 0)
        {
          this.getAuditChannelDetails();
        }
        else
          if (this.currentAuditType === 1 || this.currentAuditType === 2 || this.currentAuditType === 3)
          {
            this.getAuditChannelNameDetails();
          }
          else
            if (this.currentAuditType === 4)
            {
              this.getAuditGoalsDetails();
            }
      }
    });
  }
}
